import React, { useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Clock, Users, Zap } from "lucide-react";
import { useNotificationChoices } from "@features/ux/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

type WorkflowFilters = {
  page: number;
  limit: number;
  status?: string[];
  triggerType?: string;
  recipientType?: string;
  search_data?: string;
};

interface FilterPanelProps {
  currentFilters: WorkflowFilters;
  onApplyFilters: (filters: Partial<WorkflowFilters>) => void;
  onResetFilters: () => void;
}

export const WorkflowFilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Fetch dynamic audience types from API
  const { data: audienceType } = useNotificationChoices("recipient_type");
  const audienceTypeChoice = mapToLabelValue(audienceType);

  const form = useForm({
    defaultValues: {
      status: currentFilters.status?.[0] || "ALL_STATUS", // Convert array to single value
      triggerType: currentFilters.triggerType || "ALL_TRIGGERS",
      recipientType: currentFilters.recipientType || "ALL_RECIPIENTS",
    },
  });

  useEffect(() => {
    form.setValue("status", currentFilters.status?.[0] || "ALL_STATUS");
    form.setValue("triggerType", currentFilters.triggerType || "ALL_TRIGGERS");
    form.setValue(
      "recipientType",
      currentFilters.recipientType || "ALL_RECIPIENTS"
    );
  }, [currentFilters, form]);

  const handleApplyFilters = (data: any) => {
    console.log("🚀 Workflow filter form submitted with data:", data);

    const filtersToApply: Partial<WorkflowFilters> = {};

    if (data.status && data.status !== "ALL_STATUS") {
      filtersToApply.status = [data.status]; // Convert back to array
    }

    if (data.triggerType && data.triggerType !== "ALL_TRIGGERS") {
      filtersToApply.triggerType = data.triggerType;
    }

    if (data.recipientType && data.recipientType !== "ALL_RECIPIENTS") {
      filtersToApply.recipientType = data.recipientType;
    }

    console.log("🎯 Final workflow filters to apply:", filtersToApply);
    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    form.reset({
      status: "ALL_STATUS",
      triggerType: "ALL_TRIGGERS",
      recipientType: "ALL_RECIPIENTS",
    });
    onResetFilters();
  };

  const getTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case "time-based":
        return <Clock className="h-4 w-4 mr-2" />;
      case "event-based":
        return <Zap className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  const getRecipientIcon = (recipient: string) => {
    // You can customize icons based on the actual recipient values from API
    return <Users className="h-4 w-4 mr-2" />;
  };

  return (
    <form onSubmit={form.handleSubmit(handleApplyFilters)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {/* Status Filter */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="form-input">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUS">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Recipient Type Filter */}
        <div className="space-y-2">
          <Label>Recipient Type</Label>
          <Controller
            name="recipientType"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select recipient type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_RECIPIENTS">
                      All Recipient Types
                    </SelectItem>
                    {audienceTypeChoice?.map((audience) => (
                      <SelectItem key={audience.value} value={audience.value}>
                        <div className="flex items-center">
                          {getRecipientIcon(audience.value)}
                          {audience.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Buttons */}
        <div className="col-span-1 md:col-span-3 lg:col-span-3 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
